import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import { Box, Grid } from "@mui/material";
import { FormikProps } from "formik";
import { useEffect, useRef, useState } from "react";
import CustomSelectScroll from "../../../../../../components/CustomSelectScroll";
import Input from "../../../../../../components/Input";
import { useProviderListV2 } from "../../../../../../queries/use-provider-list-v2";
import { ProviderListItem } from "../../../../../../types/provider-types";
import CardView from "../../../patient/patient-list/patient-chart-new/assessment/summary/components/CardView";
import { UPDATE_CLAIM_FORM_TYPE } from "../claims-constants";
import { useProviderNpiAutofill } from "./use-provider-npi-autofill";
import { DEF_PAGINATION, DGT_Pagination } from "../../../../../../utils/utils";
import { getStoredRealm } from "@/utils/auth-storage";

interface ProviderDetailsClaimProps {
  formik: FormikProps<UPDATE_CLAIM_FORM_TYPE>;
  readOnly?: boolean;
}

function ProviderDetailsClaim({ formik, readOnly }: ProviderDetailsClaimProps) {
  const { values, setFieldValue, errors, touched } = formik;

  // Billing Provider is the Practice Group (Billing Entity), no
  // longer a searchable individual provider.
  // const [searchBillProviderGroupKey, setSearchBillProviderGroupKey] =
  //   useState<string>("");

  const [searchProviderKey, setSearchProviderKey] = useState<string>("");

  const [searchReferringProviderGroupKey, setSearchReferringProviderGroupKey] =
    useState<string>("");

  const [searchOrderingProviderGroupKey, setSearchOrderingProviderGroupKey] =
    useState<string>("");

  const xTENANTID = getStoredRealm() ?? "";

  const [renderingPagination, setRenderingPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [referringPagination, setReferringPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [orderingPagination, setOrderingPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);

  // A picked provider can live on a page that later scrolled out of
  // `content`, so keep every loaded provider by uuid to resolve the selection.
  const providerByUuid = useRef<Record<string, ProviderListItem>>({});

  const renderingProviderUuid = Object.keys(
    values.renderingproviderId ?? {}
  )[0];

  const { markSelected: markRenderingProviderSelected } =
    useProviderNpiAutofill({
      providerUuid: renderingProviderUuid,
      tenantId: xTENANTID,
      currentValue: values.renderingProviderNPINumber,
      setValue: (npi) => setFieldValue("renderingProviderNPINumber", npi),
    });

  const { data: providersList, isFetching: isFetchingProviders } =
    useProviderListV2({
      status: true,
      pagination: renderingPagination,
      searchString: searchProviderKey,
      xTENANTID: getStoredRealm() ?? "",
    });
  // Billing Provider resolved from the Practice Group billing-info
  // API instead of the individual provider list.
  // const { data: providersBillList } = useProviderListV2({
  //   status: true,
  //   pagination: custompagination,
  //   searchString: searchBillProviderGroupKey,
  //   xTENANTID: xTENANTID,
  // });

  const { data: providersReferringList, isFetching: isFetchingReferring } =
    useProviderListV2({
      status: true,
      pagination: referringPagination,
      searchString: searchReferringProviderGroupKey,
      xTENANTID: xTENANTID,
    });

  const { data: providersOrderingList, isFetching: isFetchingOrdering } =
    useProviderListV2({
      status: true,
      pagination: orderingPagination,
      searchString: searchOrderingProviderGroupKey,
      xTENANTID: xTENANTID,
    });

  useEffect(() => {
    [
      ...(providersList?.data?.content ?? []),
      ...(providersReferringList?.data?.content ?? []),
      ...(providersOrderingList?.data?.content ?? []),
    ].forEach((it) => {
      if (it?.uuid) providerByUuid.current[it.uuid] = it;
    });
  }, [
    providersList?.data?.content,
    providersReferringList?.data?.content,
    providersOrderingList?.data?.content,
  ]);

  // Billing Provider = Practice Group (Billing Entity), resolved onto the claim
  // by the backend. Display the practice name/NPI returned with the claim.
  const billingProviderDisplayName =
    values.practiceName ||
    Object.values(values.billingProviderId ?? {})[0] ||
    "";
  const billingProviderNpiDisplay =
    values.practiceNpi || values.billingProvideNPINumber || "";

  const referringProviderUuid = Object.keys(
    values.referringProviderId ?? {}
  )[0];
  const { markSelected: markReferringProviderSelected } =
    useProviderNpiAutofill({
      providerUuid: referringProviderUuid,
      tenantId: xTENANTID,
      currentValue: values.referringProviderNPINumber,
      setValue: (npi) => setFieldValue("referringProviderNPINumber", npi),
    });

  const orderingProviderUuid = Object.keys(values.orderingProviderId ?? {})[0];
  const { markSelected: markOrderingProviderSelected } = useProviderNpiAutofill(
    {
      providerUuid: orderingProviderUuid,
      tenantId: xTENANTID,
      currentValue: values.orderingProviderNPINumber,
      setValue: (npi) => setFieldValue("orderingProviderNPINumber", npi),
    }
  );

  return (
    <Box id={"Provider Details"}>
      <CardView
        title="Provider Details"
        titleIcon={
          <ManageAccountsOutlinedIcon
            sx={{ color: "violet" }}
            fontSize="small"
          />
        }
      >
        <Grid container spacing={2} direction={"column"} p={0.8}>
          <Grid item container spacing={2}>
            {/* Billing Provider = Practice Group (Billing Entity),
                read-only, resolved onto the claim by the backend. */}
            <Grid item xs={3}>
              <Input
                label="Billing Provider"
                textFieldProps={{
                  name: "billingProviderId",
                  placeholder: "Billing Provider",
                  disabled: true,
                  value: billingProviderDisplayName,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Input
                label="Billing Provider NPI"
                error={
                  touched?.billingProvideNPINumber &&
                  errors.billingProvideNPINumber
                }
                textFieldProps={{
                  name: "billingProvideNPINumber",
                  placeholder: "Billing Provider NPI",
                  disabled: true,
                  value: billingProviderNpiDisplay,
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <CustomSelectScroll
                label="Referring Provider"
                readOnly={readOnly}
                placeholder="Select Referring Provider"
                loading={isFetchingReferring}
                pagination={referringPagination}
                setPagination={setReferringPagination}
                totalElements={
                  providersReferringList?.data?.page?.totalElements
                }
                options={providersReferringList?.data?.content?.map((it) => ({
                  value: it?.uuid,
                  label: it?.name,
                }))}
                onSearch={(_, val) => {
                  setSearchReferringProviderGroupKey(val);
                  setReferringPagination((p) => ({ ...p, page: 0 }));
                }}
                value={
                  Object.keys(values.referringProviderId ?? {})?.[0] ??
                  undefined
                }
                onChange={(value: string | null) => {
                  const curOption =
                    providerByUuid.current[value as string] ??
                    providersReferringList?.data?.content?.find(
                      (it) => it.uuid === value
                    );
                  setFieldValue(
                    "referringProviderId",
                    curOption
                      ? {
                          [curOption.uuid]: curOption?.name,
                        }
                      : undefined
                  );
                  markReferringProviderSelected(value);
                  if (!value) setFieldValue("referringProviderNPINumber", "");
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Input
                label="Referring Provider NPI"
                error={
                  touched?.referringProviderNPINumber &&
                  errors.referringProviderNPINumber
                }
                textFieldProps={{
                  name: "referringProviderNPINumber",
                  disabled: readOnly,
                  placeholder: "Referring Provider NPI",
                  value: values?.referringProviderNPINumber ?? "",
                  onBlur: formik.handleBlur,
                  onChange: (e) =>
                    setFieldValue(
                      "referringProviderNPINumber",
                      e.target.value.replace(/\D/g, "").slice(0, 10)
                    ),
                  inputProps: { maxLength: 10, inputMode: "numeric" },
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <CustomSelectScroll
                label="Ordering Provider"
                readOnly={readOnly}
                placeholder="Select Ordering Provider"
                loading={isFetchingOrdering}
                pagination={orderingPagination}
                setPagination={setOrderingPagination}
                totalElements={providersOrderingList?.data?.page?.totalElements}
                options={providersOrderingList?.data?.content?.map((it) => ({
                  value: it?.uuid,
                  label: it?.name,
                }))}
                onSearch={(_, val) => {
                  setSearchOrderingProviderGroupKey(val);
                  setOrderingPagination((p) => ({ ...p, page: 0 }));
                }}
                value={
                  Object.keys(values.orderingProviderId ?? {})?.[0] ?? undefined
                }
                onChange={(value: string | null) => {
                  const curOption =
                    providerByUuid.current[value as string] ??
                    providersOrderingList?.data?.content?.find(
                      (it) => it.uuid === value
                    );
                  setFieldValue(
                    "orderingProviderId",
                    curOption
                      ? {
                          [curOption.uuid]: curOption?.name,
                        }
                      : undefined
                  );
                  markOrderingProviderSelected(value);
                  if (!value) setFieldValue("orderingProviderNPINumber", "");
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <Input
                label="Ordering Provider NPI"
                error={
                  touched?.orderingProviderNPINumber &&
                  errors.orderingProviderNPINumber
                }
                textFieldProps={{
                  name: "orderingProviderNPINumber",
                  disabled: readOnly,
                  placeholder: "Ordering Provider NPI",
                  value: values?.orderingProviderNPINumber ?? "",
                  onBlur: formik.handleBlur,
                  onChange: (e) =>
                    setFieldValue(
                      "orderingProviderNPINumber",
                      e.target.value.replace(/\D/g, "").slice(0, 10)
                    ),
                  inputProps: { maxLength: 10, inputMode: "numeric" },
                }}
              />
            </Grid>
            <Grid item xs={3}>
              <CustomSelectScroll
                readOnly={readOnly}
                required
                // A claim always has to name the provider it was rendered by,
                // so the field can be changed but not emptied. Clearing it left
                // the claim with a required field the user could not see was
                // gone once they had navigated on.
                clearable={false}
                name="renderingproviderId"
                label="Rendering Provider"
                placeholder="Select Rendering Provider"
                loading={isFetchingProviders}
                pagination={renderingPagination}
                setPagination={setRenderingPagination}
                totalElements={providersList?.data?.page?.totalElements}
                options={providersList?.data?.content?.map((it) => ({
                  value: it?.uuid,
                  label: it?.name,
                }))}
                onSearch={(_, val) => {
                  setSearchProviderKey(val);
                  setRenderingPagination((p) => ({ ...p, page: 0 }));
                }}
                value={
                  Object.keys(values.renderingproviderId ?? {})?.[0] ??
                  undefined
                }
                onChange={(value: string | null) => {
                  const curOption =
                    providerByUuid.current[value as string] ??
                    providersList?.data?.content?.find(
                      (it) => it.uuid === value
                    );
                  setFieldValue(
                    "renderingproviderId",
                    curOption
                      ? {
                          [curOption.uuid]: curOption?.name,
                        }
                      : undefined
                  );
                  markRenderingProviderSelected(value);
                  if (!value) setFieldValue("renderingProviderNPINumber", "");
                }}
                error={
                  touched?.renderingproviderId && errors.renderingproviderId
                }
                handleBlur={formik.handleBlur}
              />
            </Grid>
            <Grid item xs={3}>
              <Input
                required
                label="Rendering Provider NPI"
                error={
                  touched?.renderingProviderNPINumber &&
                  errors.renderingProviderNPINumber
                }
                textFieldProps={{
                  name: "renderingProviderNPINumber",
                  disabled: readOnly,
                  placeholder: "Rendering Provider NPI",
                  value: values?.renderingProviderNPINumber ?? "",
                  onBlur: formik.handleBlur,
                  onChange: (e) =>
                    setFieldValue(
                      "renderingProviderNPINumber",
                      e.target.value.replace(/\D/g, "").slice(0, 10)
                    ),
                  inputProps: { maxLength: 10, inputMode: "numeric" },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </CardView>
    </Box>
  );
}

export default ProviderDetailsClaim;
